import { buildBlackCourse } from "./blackCourses.ts";
import type { ChessConstructor } from "../chess/chessTypes.ts";
import type { OpeningId } from "../lessons/ids.ts";
import type { LessonCourse } from "../lessons/schema.ts";
import type { LegacyOpening } from "./shallow.ts";
import { whiteCourseBuilder } from "./whiteCourses.ts";

export type OpeningTable = Record<string, LegacyOpening>;

/**
 * One builder per course, in catalog order, so a course can be built on its own.
 * A course's body must be downloaded first (loadWhiteCourse, loadBlackCourse).
 */
export const COURSE_BUILDERS: Record<OpeningId, (Chess: ChessConstructor) => LessonCourse> = {
  italian: (Chess) => whiteCourseBuilder("italian")(Chess),
  quietTwoKnights: (Chess) => whiteCourseBuilder("quietTwoKnights")(Chess),
  queensGambit: (Chess) => whiteCourseBuilder("queensGambit")(Chess),
  ruyLopez: (Chess) => whiteCourseBuilder("ruyLopez")(Chess),
  english: (Chess) => whiteCourseBuilder("english")(Chess),
  london: (Chess) => whiteCourseBuilder("london")(Chess),
  alapin: (Chess) => whiteCourseBuilder("alapin")(Chess),
  vienna: (Chess) => whiteCourseBuilder("vienna")(Chess),
  scotch: (Chess) => whiteCourseBuilder("scotch")(Chess),
  fourKnights: (Chess) => whiteCourseBuilder("fourKnights")(Chess),
  reti: (Chess) => whiteCourseBuilder("reti")(Chess),
  openSicilian: (Chess) => whiteCourseBuilder("openSicilian")(Chess),
  kingsGambit: (Chess) => whiteCourseBuilder("kingsGambit")(Chess),
  pircModern: (Chess) => whiteCourseBuilder("pircModern")(Chess),
  frenchAdvance: (Chess) => whiteCourseBuilder("frenchAdvance")(Chess),
  scandinavian: (Chess) => whiteCourseBuilder("scandinavian")(Chess),
  caroAdvance: (Chess) => whiteCourseBuilder("caroAdvance")(Chess),
  caroKann: (Chess) => buildBlackCourse(Chess, "caroKann"),
  qgdBlack: (Chess) => buildBlackCourse(Chess, "qgdBlack"),
  symmetricalEnglish: (Chess) => buildBlackCourse(Chess, "symmetricalEnglish"),
  openGame: (Chess) => buildBlackCourse(Chess, "openGame"),
  sicilianNajdorf: (Chess) => buildBlackCourse(Chess, "sicilianNajdorf"),
  kingsIndian: (Chess) => buildBlackCourse(Chess, "kingsIndian"),
  frenchDefense: (Chess) => buildBlackCourse(Chess, "frenchDefense"),
  kingsEnglish: (Chess) => buildBlackCourse(Chess, "kingsEnglish"),
  petroffDefense: (Chess) => buildBlackCourse(Chess, "petroffDefense"),
  philidorDefense: (Chess) => buildBlackCourse(Chess, "philidorDefense"),
  slavDefense: (Chess) => buildBlackCourse(Chess, "slavDefense"),
  nimzoIndian: (Chess) => buildBlackCourse(Chess, "nimzoIndian"),
  scandinavianDefense: (Chess) => buildBlackCourse(Chess, "scandinavianDefense"),
  pircDefense: (Chess) => buildBlackCourse(Chess, "pircDefense"),
  qgaBlack: (Chess) => buildBlackCourse(Chess, "qgaBlack"),
  twoKnights: (Chess) => buildBlackCourse(Chess, "twoKnights"),
  englishNf6: (Chess) => buildBlackCourse(Chess, "englishNf6"),
  retiBlack: (Chess) => buildBlackCourse(Chess, "retiBlack"),
  classicalSicilian: (Chess) => buildBlackCourse(Chess, "classicalSicilian"),
  kanSicilian: (Chess) => buildBlackCourse(Chess, "kanSicilian"),
  berlinDefense: (Chess) => buildBlackCourse(Chess, "berlinDefense"),
  steinitzDefense: (Chess) => buildBlackCourse(Chess, "steinitzDefense"),
  classicalRuy: (Chess) => buildBlackCourse(Chess, "classicalRuy"),
};

export function buildCourseCatalog(
  Chess: ChessConstructor,
  _openings?: OpeningTable
): Record<OpeningId, LessonCourse> {
  return Object.fromEntries(
    Object.entries(COURSE_BUILDERS).map(([id, build]) => [id, build(Chess)])
  ) as Record<OpeningId, LessonCourse>;
}
